import { httpClient } from '@/services/http/client'
import { buildApiUrl, ApiService } from '@/config/api.config'
import type { ResidentialComplex } from '@/types/entities/residential-complex'
import type {
  ResidentialComplexFilter,
  PaginationParams,
  PaginatedResidentialComplexesResponse,
} from '@/types/filters/residential-complex-filter'
import type { ProjectType } from '@/types/enums/project-type'
import type { ProjectRank } from '@/types/enums/project-rank'
import type { Amenity } from '@/types/enums/amenity'
import type { ResidentialComplexFeature } from '@/types/enums/residential-complex-feature'
import type { City } from '@/types/entities/city'

/**
 * Упрощенный фильтр для поиска
 */
export interface SearchFilters {
  cityIds?: string[]
  citySlugs?: string[]
  projectTypes?: number[]
  numberOfRooms?: number[]
  minPrice?: number
  maxPrice?: number
}

/**
 * Значения фильтров с сервера
 */
export interface FilterValues {
  cities: City[]
  projectTypes: Array<{ value: number; displayName: string }>
}

/**
 * Параметры запроса для списка ЖК
 * Реплицирует GetResidentialComplexesRequest из backend
 */
interface GetResidentialComplexesRequest {
  pagination: {
    page: number
    perPage: number
  }
  filter?: {
    developerId?: string
    exclude?: string
    type?: ProjectType[]  // В proto: repeated entities.ProjectType type = 5
    cityId?: string[]  // В proto: repeated string city_id = 2
    citySlug?: string[]  // В proto: repeated string city_slug = 17
    numberOfRooms?: number[]  // В proto: repeated int64 number_of_rooms = 3
    price?: {
      min?: number
      max?: number
    }
    projectRank?: ProjectRank
    inboundAmenities?: Amenity[]
    features?: ResidentialComplexFeature[]
    featured?: number // 0 = INVALID (все), 1 = TRUE (только featured), 2 = FALSE (только не-featured)
  }
}

/**
 * API методы для Residential Complexes
 * Реплицирует функциональность из Flutter
 */
export const residentialComplexApi = {
  /**
   * Получить список ЖК с фильтрами и пагинацией
   * POST /api/v1/properties/residential-complexes
   */
  async list(
    filter: ResidentialComplexFilter = {},
    pagination: PaginationParams = { page: 1, perPage: 10 }
  ): Promise<PaginatedResidentialComplexesResponse<ResidentialComplex>> {
    const url = buildApiUrl(ApiService.Properties, 'properties/residential-complexes')

    // Формируем запрос как в Flutter
    // DEV-302: vinprop.common.v1.PageRequest — uint64, в protojson строки
    const request: GetResidentialComplexesRequest = {
      pagination: {
        page: pagination.page,
        perPage: pagination.perPage,
      },
    }

    // Добавляем фильтр, если есть
    if (Object.keys(filter).length > 0) {
      request.filter = {}

      if (filter.developerId) {
        request.filter.developerId = filter.developerId
      }
      if (filter.exclude) {
        request.filter.exclude = filter.exclude
      }
      if (filter.types && filter.types.length > 0) {
        request.filter.type = filter.types  // proto ожидает 'type', а не 'types'
      }
      if (filter.cityIds && filter.cityIds.length > 0) {
        request.filter.cityId = filter.cityIds  // proto ожидает 'cityId', а не 'cityIds'
      }
      if (filter.citySlugs && filter.citySlugs.length > 0) {
        request.filter.citySlug = filter.citySlugs
      }
      if (filter.numberOfRooms && filter.numberOfRooms.length > 0) {
        request.filter.numberOfRooms = filter.numberOfRooms
      }
      if (filter.price) {
        request.filter.price = filter.price
      }
      if (filter.projectRank) {
        request.filter.projectRank = filter.projectRank
      }
      if (filter.inboundAmenities && filter.inboundAmenities.length > 0) {
        request.filter.inboundAmenities = filter.inboundAmenities
      }
      if (filter.features && filter.features.length > 0) {
        request.filter.features = filter.features
      }
      if (filter.featured !== undefined) {
        // undefined/null = 0 (INVALID, все комплексы)
        // true = 1 (только featured)
        // false = 2 (только не-featured)
        request.filter.featured = filter.featured === true ? 1 : filter.featured === false ? 2 : 0
      }
    }

    // DEV-302: vinprop.common.v1.PageInfo — uint64-поля protojson отдаёт строками
    const response = await httpClient.post<{
      residentialComplexes: ResidentialComplex[]
      pagination: {
        page: number | string
        perPage: number | string
        total: number | string
      }
    }>(url, request)

    // Маппинг proto-ответа к внутреннему формату (строки uint64 → числа)
    const raw = response.pagination || { page: 1, perPage: pagination.perPage, total: 0 }
    const page = Number(raw.page) || 1
    const responsePerPage = Number(raw.perPage) || pagination.perPage || 10
    const total = Number(raw.total) || 0
    const totalPages = responsePerPage > 0 ? Math.ceil(total / responsePerPage) : 0

    return {
      complexes: response.residentialComplexes || [],
      pagination: {
        currentPage: page,
        perPage: responsePerPage,
        totalPages,
        totalCount: total,
      },
    }
  },

  /**
   * Получить ЖК по слагу города и слагу
   * GET /api/v1/properties/rc/{city_slug}/{slug}
   */
  async getBySlug(citySlug: string, slug: string): Promise<ResidentialComplex> {
    const url = buildApiUrl(ApiService.Properties, `properties/rc/${citySlug}/${slug}`)
    console.log(`[API] Loading complex by ${citySlug}/${slug} from ${url}`)
    const response = await httpClient.get<{ residentialComplex: ResidentialComplex }>(url)
    console.log(`[API] Complex loaded:`, response.residentialComplex)
    return response.residentialComplex
  },

  /**
   * Получить featured ЖК
   * POST /api/v1/properties/residential-complexes с filter.featured = 1
   */
  async getFeatured(limit = 10): Promise<ResidentialComplex[]> {
    const response = await this.list(
      { featured: true },
      { page: 1, perPage: limit }
    )
    return response.complexes
  },

  /**
   * Поиск ЖК с упрощенными фильтрами (для SearchBar)
   * Использует тот же эндпоинт, что и list()
   */
  async search(
    filters: SearchFilters,
    page = 1,
    perPage = 12
  ): Promise<{
    residentialComplexes: ResidentialComplex[]
    pagination: {
      page: number
      total: number
      totalPages: number
      perPage: number
    }
  }> {
    // Конвертируем SearchFilters в ResidentialComplexFilter
    const complexFilter: ResidentialComplexFilter = {}

    if (filters.cityIds && filters.cityIds.length > 0) {
      complexFilter.cityIds = filters.cityIds
    }
    if (filters.citySlugs && filters.citySlugs.length > 0) {
      complexFilter.citySlugs = filters.citySlugs
    }
    if (filters.projectTypes && filters.projectTypes.length > 0) {
      complexFilter.types = filters.projectTypes as ProjectType[]
    }
    if (filters.numberOfRooms && filters.numberOfRooms.length > 0) {
      complexFilter.numberOfRooms = filters.numberOfRooms
    }
    if (filters.minPrice !== undefined || filters.maxPrice !== undefined) {
      complexFilter.price = {
        min: filters.minPrice,
        max: filters.maxPrice,
      }
    }

    const response = await this.list(complexFilter, { page, perPage })

    return {
      residentialComplexes: response.complexes,
      pagination: {
        page: response.pagination.currentPage,
        total: response.pagination.totalCount,
        totalPages: response.pagination.totalPages,
        perPage: response.pagination.perPage,
      },
    }
  },

  /**
   * Получить возможные значения фильтров
   * TODO: Реализовать на backend отдельный эндпоинт
   * Пока возвращаем моковые данные
   * 
   * NOTE: displayName должен заполняться в компонентах через i18n
   * @param t - функция перевода из useI18n()
   */
  async getFilterValues(t?: (key: string) => string): Promise<FilterValues> {
    // TODO: Заменить на реальный запрос к API когда будет готов эндпоинт
    const projectTypes = [
      { value: 1, displayName: t ? t('search.project_types.residential_complex') : 'Residential Complex' },
      { value: 2, displayName: t ? t('search.project_types.gated_community') : 'Gated Community' },
      { value: 3, displayName: t ? t('search.project_types.holiday_homes') : 'Holiday Homes' },
      { value: 4, displayName: t ? t('search.project_types.commercial_real_estate') : 'Commercial Real Estate' },
    ]
    
    return {
      cities: [],
      projectTypes,
    }
  },
}
